import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

/** Default recognition language. English matches the composers' placeholders. */
const DEFAULT_LANG = "en-US";

/**
 * Append freshly recognized speech to an existing draft with a single joining
 * space, avoiding a leading space on an empty draft or a double space when the
 * draft already ends in whitespace. Exported so tests exercise the real logic.
 */
export function appendTranscript(existing: string, addition: string): string {
  const trimmedAddition = addition.trim();
  if (trimmedAddition.length === 0) {
    return existing;
  }
  // A whitespace-only draft has no real content, so start fresh rather than
  // prefixing the speech with the stray spaces.
  if (existing.trim().length === 0) {
    return trimmedAddition;
  }
  const needsSpace = !/\s$/.test(existing);
  return `${existing}${needsSpace ? " " : ""}${trimmedAddition}`;
}

/** Friendly copy for the Web Speech API's terse error codes. */
function describeVoiceError(code: string): string {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone access is blocked.";
    case "no-speech":
      return "No speech detected.";
    case "audio-capture":
      return "No microphone found.";
    case "network":
      return "Voice service is unreachable.";
    default:
      return "Voice input failed.";
  }
}

/**
 * Tooltip copy for a voice trigger, shared so the drawer and page composers
 * describe the same states identically. Surfaces the last recognizer error so a
 * silently-stopped mic (e.g. denied permission) is not mistaken for idle.
 */
export function describeVoiceState(
  voice: Pick<VoiceInput, "isSupported" | "listening" | "error">,
): string {
  if (!voice.isSupported) {
    return "Voice input not supported in this browser";
  }
  if (voice.listening) {
    return "Stop listening";
  }
  if (voice.error !== null) {
    return `${describeVoiceError(voice.error)} Tap to try again.`;
  }
  return "Voice input";
}

/** Resolve the (possibly vendor-prefixed) SpeechRecognition constructor. */
function getSpeechRecognition(): SpeechRecognitionConstructor | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.SpeechRecognition ?? window.webkitSpeechRecognition;
}

export interface VoiceInput {
  /** Whether the browser exposes the Web Speech API at all. */
  isSupported: boolean;
  /** True while the microphone is actively transcribing. */
  listening: boolean;
  /** The current, not-yet-final transcript (for optional live display). */
  interimText: string;
  /** The last error code reported by the recognizer, or null. */
  error: string | null;
  /** Begin listening. No-op when unsupported or already listening. */
  start: () => void;
  /** Stop listening. No-op when unsupported or not listening. */
  stop: () => void;
  /**
   * Stop listening and discard any pending speech, including the not-yet-final
   * segment, without delivering it to `onResult`. Used when a typed edit should
   * replace the previewed interim text rather than append it. By default the
   * `onEnd` callback is suppressed so the consumer does not treat this as a
   * finished turn; pass `{ silent: false }` to let `onEnd` fire (e.g. so a
   * paused dialog can reopen its command mic after the native end).
   */
  abort: (options?: Readonly<{ silent?: boolean }>) => void;
  /** Start when idle, stop when listening. */
  toggle: () => void;
}

/**
 * Thin wrapper over the browser Web Speech API for push-to-talk voice input.
 *
 * Each hook instance owns its own `SpeechRecognition`, so multiple composers
 * can mount the hook without fighting over a shared global recognizer. Final
 * results are handed to `onResult`; the caller decides how to fold them into
 * its draft (see `appendTranscript`). Interim text is exposed separately for
 * callers that want to preview speech before it settles.
 *
 * The API is free but browser-gated: Chromium and Safari support it, Firefox
 * does not. Consumers should read `isSupported` and disable their trigger when
 * it is false rather than assuming the mic works.
 */
/** Whether this browser exposes a SpeechRecognition constructor. */
export function isVoiceInputSupported(): boolean {
  return getSpeechRecognition() !== undefined;
}

export function useVoiceInput(
  options: Readonly<{
    onResult: (finalText: string) => void;
    /** Fires after the recognizer stops, once final results have been delivered. */
    onEnd?: () => void;
    lang?: string;
  }>,
): VoiceInput {
  const { onResult, onEnd, lang = DEFAULT_LANG } = options;

  const Recognition = useMemo(() => getSpeechRecognition(), []);
  const isSupported = Recognition !== undefined;

  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  /** Mirrors `listening` so stop-then-start in the same tick is not a no-op. */
  const listeningRef = useRef(false);
  /** Set by `abort()` so the resulting `onend` does not fire the `onEnd` callback. */
  const suppressEndRef = useRef(false);
  /**
   * Set by `abort()` so the `aborted` error the recognizer reports before `onend`
   * is recognized as an intentional teardown and not surfaced as a failure.
   */
  const intentionalAbortRef = useRef(false);
  /**
   * True from `stop()` / `abort()` until the native `onend` arrives. The
   * recognizer is still shutting down in this window, so calling `start()` now
   * throws `InvalidStateError`; the restart is queued and flushed on `onend`.
   */
  const stoppingRef = useRef(false);
  /** A `start()` requested while still stopping, replayed once the mic is idle. */
  const pendingStartRef = useRef(false);
  // Keep the latest onResult without re-creating the recognizer each render.
  const onResultRef = useRef(onResult);
  const onEndRef = useRef(onEnd);
  useEffect(() => {
    onResultRef.current = onResult;
    onEndRef.current = onEnd;
  }, [onResult, onEnd]);

  // The actual `recognition.start()`, with no listening/stopping guard: the
  // public `start` adds those, and `onend` replays a queued restart through
  // this once the recognizer has truly settled idle.
  const beginListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition === null) {
      return;
    }
    setError(null);
    setInterimText("");
    stoppingRef.current = false;
    pendingStartRef.current = false;
    try {
      recognition.start();
    } catch {
      // `start()` throws if called while already started; treat as a no-op.
    }
    listeningRef.current = true;
    setListening(true);
  }, []);

  // Lazily build the recognizer once and wire its event handlers.
  useEffect(() => {
    if (Recognition === undefined) {
      return undefined;
    }

    const recognition = new Recognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) {
          onResultRef.current(transcript);
        } else {
          interim += transcript;
        }
      }
      setInterimText(interim);
    };

    recognition.onerror = (event) => {
      // `abort()` surfaces as an `aborted` error just before `onend`. That is the
      // intentional teardown of an interim edit, not a recognition failure, so
      // swallow it (otherwise the mic tooltip would read "Voice input failed").
      // `onend`, which always follows, clears the listening/interim state. A
      // genuine `aborted` outside an intentional abort still falls through.
      if (event.error === "aborted" && intentionalAbortRef.current) {
        intentionalAbortRef.current = false;
        return;
      }
      intentionalAbortRef.current = false;
      setError(event.error);
      listeningRef.current = false;
      setListening(false);
      setInterimText("");
    };

    recognition.onend = () => {
      listeningRef.current = false;
      setListening(false);
      setInterimText("");
      stoppingRef.current = false;
      // Safety net: if the recognizer ended without first firing the `aborted`
      // error, clear the intentional-abort flag so it cannot leak into a later cycle.
      intentionalAbortRef.current = false;
      const suppressed = suppressEndRef.current;
      suppressEndRef.current = false;
      // A restart queued while the recognizer was still stopping runs now that
      // it is idle, so `start()` no longer throws. The replay stands in for the
      // end, so the consumer's `onEnd` is not also fired for this cycle.
      if (pendingStartRef.current) {
        pendingStartRef.current = false;
        beginListening();
        return;
      }
      if (suppressed) {
        return;
      }
      onEndRef.current?.();
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.onstart = null;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, [Recognition, lang, beginListening]);

  const start = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition === null || listeningRef.current) {
      return;
    }
    if (stoppingRef.current) {
      // The previous session's native `onend` has not arrived yet, so starting
      // now would throw. Queue the restart; `onend` replays it once idle.
      pendingStartRef.current = true;
      return;
    }
    beginListening();
  }, [beginListening]);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    // A stop request must cancel any restart queued while the recognizer is
    // still shutting down; clearing it before the idle guard keeps a pending
    // native `onend` from reopening the mic after stop() (e.g. the final stop in
    // start(); stop(); start(); stop()).
    pendingStartRef.current = false;
    if (recognition === null || !listeningRef.current) {
      return;
    }
    recognition.stop();
    stoppingRef.current = true;
    listeningRef.current = false;
    setListening(false);
    setInterimText("");
  }, []);

  const abort = useCallback((options?: Readonly<{ silent?: boolean }>) => {
    const recognition = recognitionRef.current;
    // Like stop(), an abort request cancels a queued restart before the idle
    // guard so a pending native `onend` cannot reopen the mic afterwards.
    pendingStartRef.current = false;
    if (recognition === null || !listeningRef.current) {
      return;
    }
    // `abort()` (unlike `stop()`) discards the in-progress segment instead of
    // delivering it as a final result. By default the suppress flag drops the
    // `onEnd` it triggers so the dialog does not treat this as a finished turn;
    // `{ silent: false }` keeps `onEnd` so a paused dialog can reopen its
    // command mic after the native end.
    if (options?.silent ?? true) {
      suppressEndRef.current = true;
    }
    // Mark the `aborted` error the recognizer is about to report as intentional
    // so `onerror` does not record it as a failure.
    intentionalAbortRef.current = true;
    recognition.abort();
    stoppingRef.current = true;
    listeningRef.current = false;
    setListening(false);
    setInterimText("");
  }, []);

  const toggle = useCallback(() => {
    if (listening) {
      stop();
    } else {
      start();
    }
  }, [listening, start, stop]);

  return { isSupported, listening, interimText, error, start, stop, abort, toggle };
}

/**
 * Voice input wired straight into a text draft: every final transcript is
 * appended to the current draft via {@link appendTranscript}. The shared way
 * both composers fold speech into their input so the behavior stays identical.
 */
export function useDraftVoiceInput(
  setDraft: Dispatch<SetStateAction<string>>,
  lang?: string,
  onEnd?: () => void,
): VoiceInput {
  return useVoiceInput({
    onResult: (text) => setDraft((current) => appendTranscript(current, text)),
    onEnd,
    lang,
  });
}
